import React, { useEffect, useState } from 'react';
import { ToggleLeft, Loader2, CheckCircle, AlertTriangle, Shield } from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { supabase } from '../lib/supabase';

interface FeatureFlag {
  id: string;
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  updated_by: string | null;
  updated_at: string;
}

export const FeatureFlagsPage: React.FC = () => {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    loadFlags();
  }, []);

  const loadFlags = async () => {
    setLoading(true);
    try {
      const { data, error } = await adminDb.from('feature_flags').select('*').order('key');
      if (error) throw error;
      setFlags(data || []);
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const toggleFlag = async (flag: FeatureFlag) => {
    setToggling(flag.id);
    try {
      const session = await supabase.auth.getSession();
      const email = session.data.session?.user?.email || 'unknown';

      const { error } = await supabase
        .from('feature_flags')
        .update({ enabled: !flag.enabled, updated_by: email, updated_at: new Date().toISOString() })
        .eq('id', flag.id);
      if (error) throw error;

      // Log to audit
      await adminDb.from('audit_logs').insert({
        admin_email: email,
        action: 'feature_flag.toggle',
        resource_type: 'feature_flag',
        resource_id: flag.key,
        old_value: { enabled: flag.enabled },
        new_value: { enabled: !flag.enabled },
      });

      setMessage({ text: `${flag.label} ${!flag.enabled ? 'aktiviert' : 'deaktiviert'}.`, type: 'success' });
      await loadFlags();
    } catch (err: any) {
      setMessage({ text: `Fehler: ${err.message}`, type: 'error' });
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><ToggleLeft size={24} /> Feature Flags</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  const enabledCount = flags.filter(f => f.enabled).length;

  return (
    <div>
      <div className="page-header">
        <h1><ToggleLeft size={24} /> Feature Flags</h1>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-4`}>
          {message.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="grid-cols-3 mb-6">
        <div className="kpi-card">
          <span className="kpi-label">Gesamt</span>
          <span className="kpi-value">{flags.length}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Aktiviert</span>
          <span className="kpi-value" style={{ color: 'var(--success)' }}>{enabledCount}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">Deaktiviert</span>
          <span className="kpi-value" style={{ color: 'var(--text-muted)' }}>{flags.length - enabledCount}</span>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-col gap-1">
          {flags.map(flag => (
            <div key={flag.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', borderBottom: '1px solid var(--border-light)',
              transition: 'background 0.15s',
            }}>
              <div style={{ flex: 1 }}>
                <div className="flex items-center gap-3">
                  <h4>{flag.label}</h4>
                  <span className="text-mono text-muted">{flag.key}</span>
                </div>
                <p className="text-sm text-muted mt-1">{flag.description}</p>
                {flag.updated_by && (
                  <p className="text-xs text-muted mt-1">
                    Zuletzt: {flag.updated_by} — {new Date(flag.updated_at).toLocaleString('de-DE')}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {flag.key === 'maintenance_mode' && flag.enabled && (
                  <span className="badge badge-danger"><Shield size={10} /> AKTIV</span>
                )}
                <button
                  onClick={() => toggleFlag(flag)}
                  disabled={toggling === flag.id}
                  style={{
                    width: 52, height: 28, borderRadius: 14, border: 'none', cursor: 'pointer',
                    background: flag.enabled ? 'var(--success)' : 'var(--border)',
                    position: 'relative', transition: 'background 0.2s',
                    opacity: toggling === flag.id ? 0.5 : 1,
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: '50%', background: 'white',
                    position: 'absolute', top: 3,
                    left: flag.enabled ? 27 : 3,
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
